/**
 * Pick A Card — cart integrity layer.
 *
 * Owns everything that touches the cart or persisted state:
 * change detection, the mutation lock, the qualifying predicate,
 * reward application, and gift guarding.
 *
 * Loads on every page. Deliberately independent of the popup UI in
 * assets/pick-a-card.js — a visitor can win gifts in one session and
 * empty their cart in another, and those gift lines still need guarding.
 *
 * Public API: window.THHPickACard (see the return value at the bottom).
 */
(function () {
  if (window.THHPickACard) return;

  var STORAGE_KEY = 'thh_pick_a_card_v1';
  var GIFT_PROPERTY = 'Gift';
  var GIFT_VALUE = 'Pick A Card';
  var PENDING_MAX_AGE_MS = 30 * 24 * 60 * 60 * 1000;
  var DEBOUNCE_MS = 150;
  var LOCK_TIMEOUT_MS = 1500;
  var CART_PATH_RE = /\/cart\/(add|change|update|clear)(\.js)?(\?|$)/;

  var config = { minimumSubtotalCents: 0 };
  var started = false;   // declared here, not near start() — see Task 3 Step 5

  function routes() {
    var r = (window.theme && window.theme.routes) || {};
    return {
      cart: (r.cart_url || '/cart') + '.js',
      add: (r.cart_add_url || '/cart/add') + '.js',
      update: (r.cart_update_url || '/cart/update') + '.js'
    };
  }

  function log(message, error) {
    if (window.console && console.warn) console.warn('[pick-a-card] ' + message, error || '');
  }

  // ---- persisted state ----

  // Durability contract: State is best-effort, not durable.
  //
  // Every method below swallows localStorage failures by design — some
  // privacy modes (e.g. Safari private browsing, cookies/storage blocked)
  // make localStorage.getItem/setItem/removeItem throw outright, and this
  // feature must keep working (degraded to per-pageview state) rather than
  // break the cart or the page. That is intentional and must not change.
  //
  // A consequence: a patch() call that returns without throwing, and even
  // its returned merged record, does NOT prove the write reached storage.
  // The setItem failure is only logged, never surfaced to the caller.
  // Callers must treat persistence as advisory — stay correct even if
  // state silently resets between page loads (e.g. because a previous
  // patch() was swallowed) rather than assuming a return value means data
  // survived.
  //
  // Also: null is a special value, not a normal one. Passing null for a
  // key in patch(changes) deletes that key from the record. No field may
  // therefore use null as a meaningful value distinct from "absent" —
  // doing so would make that value indistinguishable from a delete.
  var State = {
    get: function () {
      var record;
      try {
        var raw = localStorage.getItem(STORAGE_KEY);
        record = raw ? JSON.parse(raw) : null;
      } catch (e) {
        record = null;
      }
      if (!record || typeof record !== 'object' || Array.isArray(record)) record = {};

      // Expire stale reservations rather than owing gifts forever.
      // Keyed on pendingAt, which is restamped every time the reservation is
      // touched — not on playedAt, which never moves. A gift re-reserved on
      // day 29 gets a fresh window instead of expiring the next day. The
      // `|| playedAt` fallback covers records written before pendingAt
      // existed, and the final `|| 0` expires an orphaned reservation that
      // has neither timestamp (localStorage cleared while the Shopify cart
      // survived) rather than owing it forever.
      // This expiry is applied to the record returned from this call only —
      // it does not write back. Raw localStorage still holds the expired
      // giftPending/pendingAt/injectAttempts keys until something later
      // calls patch() or clear(); inspecting storage directly can still show
      // an "expired" reservation until then.
      // Number(...) || 0 guards against a corrupted, non-numeric timestamp:
      // without it, Date.now() - stamp would be NaN, the ">" comparison
      // would always be false, and this safety net would silently stop
      // expiring anything.
      var stamp = Number(record.pendingAt || record.playedAt || 0) || 0;
      if (record.giftPending && Date.now() - stamp > PENDING_MAX_AGE_MS) {
        delete record.giftPending;
        delete record.pendingAt;
        delete record.injectAttempts;
      }
      return record;
    },

    patch: function (changes) {
      var next = State.get();
      for (var key in changes) {
        if (!Object.prototype.hasOwnProperty.call(changes, key)) continue;
        if (changes[key] === null) delete next[key];
        else next[key] = changes[key];
      }
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
      } catch (e) {
        log('could not persist state', e);
      }
      return next;
    },

    clear: function () {
      // Empty catch is deliberate: clearing is best-effort. If removeItem
      // throws (storage blocked/unavailable), there is nothing to recover —
      // the next get() will still handle a missing/unreadable key safely.
      try { localStorage.removeItem(STORAGE_KEY); } catch (e) {}
    }
  };

  // ---- cart predicates ----

  /**
   * Gifts granted by THIS feature, matched against a closed list of sources.
   *
   * Deliberately not "any non-empty Gift property". GiftGuard DELETES what
   * this matches, and other apps add their own cart lines — Rebuy adds a
   * Variety Pack at a threshold, for instance. Matching loosely would mean
   * that the day any app wrote a property called Gift, we would start
   * removing their line from the cart. A closed list cannot do that.
   *
   * The value records which of our systems granted it, so the popup and the
   * ladder can be reconciled independently while the guard still protects
   * both: a cart with no paid item loses every gift of ours, whichever
   * granted it.
   */
  var GIFT_SOURCES = [GIFT_VALUE, 'Ladder', 'Buy 2 Get 1', 'Mocktail Gift'];

  function isOurGift(item) {
    if (!item || !item.properties) return false;
    return GIFT_SOURCES.indexOf(item.properties[GIFT_PROPERTY]) !== -1;
  }

  /** Gifts from one specific source. */
  function isGiftFrom(item, source) {
    return !!(item && item.properties && item.properties[GIFT_PROPERTY] === source);
  }

  // "Foreign" = belongs to something other than this feature. The Good Mood
  // PDP auto-adds its own free gift line carrying an _auto_gift property
  // (sections/good-mood-product.liquid:5288-5294). Two invariants, and note
  // they pull in opposite directions from isOurGift's: never count a foreign
  // gift as a paid item, and never remove one. GiftGuard deletes only lines
  // where isOurGift is true — reading these two backwards would either delete
  // another app's gift or leave ours in a zero-value cart.
  function isForeignGift(item) {
    return !!(item && item.properties && item.properties._auto_gift);
  }

  // original_line_price, NOT final_line_price. A discount this game awarded
  // must never drag the subtotal back under the minimum and revoke the gifts
  // it just granted. Pre-discount also means a 100%-off item still counts as
  // a real paid line, which is correct — the customer chose it.
  function isPaid(item) {
    return !isOurGift(item) && !isForeignGift(item) && item.original_line_price > 0;
  }

  /** Pre-discount subtotal of paid lines. The one place this is calculated. */
  function paidSubtotal(cart) {
    if (!cart || !Array.isArray(cart.items)) return 0;
    return cart.items.filter(isPaid).reduce(function (sum, item) {
      return sum + item.original_line_price;
    }, 0);
  }

  /**
   * The hard rule: is anything actually being paid for?
   *
   * This is the one that protects the business. A cart holding only $0.00
   * gifts can be checked out for the price of shipping, so no gift of any
   * source may survive a cart with no paid line.
   */
  function hasPaidItem(cart) {
    return !!(cart && Array.isArray(cart.items) && cart.items.filter(isPaid).length > 0);
  }

  /**
   * The soft rule: the POPUP's own minimum-subtotal threshold.
   *
   * Deliberately separate from hasPaidItem. This threshold belongs to Pick A
   * Card and must not govern other gift sources — the gift ladder qualifies by
   * being on a subscription plan, and a $99 30-Nights plan against a $130
   * popup minimum would otherwise have its gifts refused and stripped by a
   * rule that was never about it.
   */
  function qualifies(cart) {
    if (!hasPaidItem(cart)) return false;
    return paidSubtotal(cart) >= config.minimumSubtotalCents;
  }

  // Resolves to null on EVERY failure — non-ok response, malformed body, or a
  // rejected fetch. Callers must never mistake a failed read for an empty
  // cart: that distinction is what stops GiftGuard deleting gifts on a network
  // blip. The try/catch covers the one path .catch cannot, a synchronous throw
  // if fetch is missing entirely, so the contract holds without exception.
  function readCart() {
    try {
      return fetch(routes().cart, {
        headers: { Accept: 'application/json' },
        credentials: 'same-origin'
      })
        .then(function (response) { return response.ok ? response.json() : null; })
        .then(function (cart) { return (cart && Array.isArray(cart.items)) ? cart : null; })
        .catch(function () { return null; });
    } catch (e) {
      log('cart read unavailable', e);
      return Promise.resolve(null);
    }
  }

  // ---- gift add and removal primitives ----

  function postJSON(url, body) {
    return fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify(body)
    }).then(function (response) {
      return response.ok ? response.json().catch(function () { return {}; }) : null;
    }).catch(function () { return null; });
  }

  /**
   * The line property that marks a gift as ours and says who granted it.
   *
   * The source matters as much as the marker: GiftGuard removes an
   * unreserved "Pick A Card" line, because the popup only ever adds one it
   * has reserved. Labelling a ladder gift with the popup's value therefore
   * had the guard delete it moments after the ladder added it.
   */
  function giftProperties(source) {
    var props = {};
    props[GIFT_PROPERTY] = source || GIFT_VALUE;
    return props;
  }

  // Shopify fails the whole batch if any single line is invalid, so one
  // sold-out gift would silently cost the visitor every other prize. The
  // fallback keeps the ones that can land.
  function addGiftsBatch(variantIds, source) {
    // A one-item "batch" that fails would be re-sent byte-identically by the
    // per-item fallback, to the same URL with nothing changed in between —
    // two POSTs where one can only fail twice. Go straight to the per-item
    // path, which reports the same {added, failed} shape.
    if (variantIds.length <= 1) return addGiftsOneByOne(variantIds, source);

    var items = variantIds.map(function (id) {
      return { id: Number(id), quantity: 1, properties: giftProperties(source) };
    });
    return postJSON(routes().add, { items: items }).then(function (result) {
      if (result) return { added: variantIds.slice(), failed: [] };
      return addGiftsOneByOne(variantIds, source);
    });
  }

  function addGiftsOneByOne(variantIds, source) {
    var added = [];
    var failed = [];
    return variantIds.reduce(function (chain, id) {
      return chain.then(function () {
        return postJSON(routes().add, {
          items: [{ id: Number(id), quantity: 1, properties: giftProperties(source) }]
        }).then(function (result) {
          if (result) added.push(id);
          else failed.push(id);
        });
      });
    }, Promise.resolve()).then(function () {
      if (failed.length) log('some gifts could not be added: ' + failed.join(', '));
      return { added: added, failed: failed };
    });
  }

  // One request keyed by line `key`. Never sequential /cart/change.js by
  // index — removing line 2 renumbers line 3 to line 2 and the next call
  // deletes the wrong item.
  function removeGiftLines(giftItems) {
    var updates = {};
    giftItems.forEach(function (item) { updates[item.key] = 0; });
    return postJSON(routes().update, { updates: updates });
  }

  // ---- reward application ----

  // /discount/CODE 302-redirects, so without ?redirect the browser downloads a
  // whole HTML page to set one cookie. /cart.js is a cheap destination.
  function applyDiscount(code) {
    if (!code) return Promise.resolve(false);
    return fetch('/discount/' + encodeURIComponent(code) + '?redirect=/cart.js', {
      credentials: 'same-origin'
    }).then(function (response) { return response.ok; })
      .catch(function () { return false; });
  }

  /**
   * Reservations must UNION, never overwrite. Overwriting loses gifts:
   * injectPending([A,B]) partially fails leaving pending [B]; the cart then
   * stops qualifying and guardGifts reserves the in-cart gift A; a plain
   * write of [A] drops B permanently.
   */
  function reserve(variantIds) {
    var existing = State.get().giftPending || [];
    var merged = existing.slice();
    variantIds.forEach(function (id) {
      if (merged.map(String).indexOf(String(id)) === -1) merged.push(id);
    });
    // injectAttempts resets here. The counter is global but reservations are
    // per-variant-set, so a count accrued against a dead variant would
    // otherwise carry into the next, unrelated reservation and drop it
    // without a single POST. Safe to reset: guardGifts only re-reserves
    // gifts actually present in the cart, so a dead variant cannot re-arm.
    State.patch({
      giftPending: merged.length ? merged : null,
      pendingAt: Date.now(),
      injectAttempts: null
    });
    return merged;
  }

  function clearReserved(variantIds) {
    var remaining = (State.get().giftPending || []).filter(function (id) {
      return variantIds.map(String).indexOf(String(id)) === -1;
    });
    State.patch({ giftPending: remaining.length ? remaining : null });
  }

  // Dedupe is keyed on the gift PROPERTY, never on variant id alone — a
  // customer who legitimately bought that product must still be able to win it.
  /** Ids present in `all` but absent from `subset`. */
  function idsNotIn(all, subset) {
    var keys = subset.map(String);
    return all.filter(function (id) { return keys.indexOf(String(id)) === -1; });
  }

  function pendingAfterDedupe(cart, variantIds) {
    var alreadyGifted = {};
    cart.items.filter(isOurGift).forEach(function (item) {
      alreadyGifted[String(item.variant_id)] = true;
    });
    return variantIds.filter(function (id) { return !alreadyGifted[String(id)]; });
  }

  /**
   * Apply won prizes.
   * @param {Array} prizes - [{ type: 'free_gift', variantId, label } | { type: 'discount_code', code, label }]
   * @returns {Promise<{applied, reserved, failed, discountCode, discountApplied, paidSubtotalCents, shortfallCents}>}
   */
  function applyRewards(prizes) {
    var list = Array.isArray(prizes) ? prizes : [];
    var giftIds = list.filter(function (p) { return p.type === 'free_gift' && p.variantId; })
                      .map(function (p) { return p.variantId; });
    var discount = list.filter(function (p) { return p.type === 'discount_code' && p.code; })[0];
    var code = discount ? discount.code : null;

    var outcome = {
      applied: [], reserved: [], failed: [],
      discountCode: code, discountApplied: false,
      // shortfallCents drives the reserved-gifts copy. paidSubtotalCents is
      // exposed for debugging and future use; nothing reads it today, which
      // is intentional and not missing wiring. Both are computed here, from
      // the same predicate internals, so the UI never reimplements isPaid.
      paidSubtotalCents: 0,
      shortfallCents: 0
    };

    return applyDiscount(code)
      .then(function (ok) {
        outcome.discountApplied = ok;
        if (code) State.patch({ discountCode: code });
        return readCart();
      })
      .then(function (cart) {
        if (cart) {
          outcome.paidSubtotalCents = paidSubtotal(cart);
          outcome.shortfallCents = Math.max(
            0, config.minimumSubtotalCents - outcome.paidSubtotalCents
          );
        }

        // A failed read means we cannot tell whether the cart qualifies, so
        // reserve rather than add. Reserving is self-healing: injectPending
        // fires the moment the next cart signal shows a qualifying cart.
        // Adding blind is not — it can drop $0.00 lines into an empty cart,
        // the one outcome GiftGuard exists to prevent.
        if (!cart || !qualifies(cart)) {
          outcome.reserved = reserve(giftIds);
          return outcome;
        }

        var toAdd = pendingAfterDedupe(cart, giftIds).filter(notSuperseded(cart));

        // Anything dropped by the dedupe is already in the cart, so it is
        // fulfilled, not pending. Clear it now rather than only in the
        // all-present branch below: with a mixed set the reservation for a
        // gift the visitor already holds would otherwise survive until a
        // later cycle happened to produce an empty toAdd, or until the
        // 30-day expiry. injectPending does the same, deliberately.
        var alreadyPresent = idsNotIn(giftIds, toAdd);
        if (alreadyPresent.length) clearReserved(alreadyPresent);

        if (!toAdd.length) {
          outcome.applied = giftIds.slice();
          return outcome;
        }

        acquireLock();
        return addGiftsBatch(toAdd).then(function (result) {
          // Gifts already in the cart count as applied — they are there, which
          // is all the success screen claims.
          outcome.applied = alreadyPresent.concat(result.added);
          outcome.failed = result.failed;
          clearReserved(result.added);
          if (result.failed.length) outcome.reserved = reserve(result.failed);
          announceCartUpdate();
          return outcome;
        });
      });
  }

  // ---- change detection and the mutation lock ----

  var cycleCount = 0;          // open theme-events.js refresh cycles
  var lockHeld = false;
  var ourDispatchDone = false; // has OUR theme:update:cart gone out yet?
  var lockTimer = null;

  // theme-events.js dispatches shapes:cart:lock synchronously at the top of
  // its handler (line 13) and shapes:cart:unlock in its finally (line 48).
  // Counting the pair lets us tell our own settled write from a foreign one.
  document.addEventListener('shapes:cart:lock', function () {
    cycleCount += 1;
  });

  document.addEventListener('shapes:cart:unlock', function () {
    // Clamp at zero. Our listeners can attach mid-cycle and see an unmatched
    // unlock; without the clamp the counter goes negative and never returns
    // to zero again, so every release silently falls back to the timeout.
    cycleCount = Math.max(0, cycleCount - 1);
    maybeRelease();
  });

  function acquireLock() {
    lockHeld = true;
    ourDispatchDone = false;
    if (lockTimer) clearTimeout(lockTimer);
    // Fallback only: theme-events.js is async defer, so its section fetch can
    // hang or arrive late. The normal release is the cycle below.
    lockTimer = setTimeout(releaseLock, LOCK_TIMEOUT_MS);
  }

  function maybeRelease() {
    if (!lockHeld) return;
    // Do not release on a foreign cycle that was already open when we
    // acquired — that is the normal THH deferred-add path, not a corner case.
    if (!ourDispatchDone) return;
    if (cycleCount > 0) return;
    releaseLock();
  }

  function releaseLock() {
    if (lockTimer) clearTimeout(lockTimer);
    lockTimer = null;
    lockHeld = false;
    ourDispatchDone = false;
  }

  /** Tell the theme to re-render the cart, and mark our write as settled. */
  function announceCartUpdate() {
    document.dispatchEvent(new CustomEvent('theme:update:cart', { bubbles: true }));
    ourDispatchDone = true;
    maybeRelease();
  }

  var changeListeners = [];
  var debounceTimer = null;

  function scheduleChange() {
    if (lockHeld) return;                 // our own writes are self-reported
    if (debounceTimer) clearTimeout(debounceTimer);
    debounceTimer = setTimeout(runChange, DEBOUNCE_MS);
  }

  function runChange() {
    debounceTimer = null;
    if (lockHeld) return;
    readCart().then(function (cart) {
      if (!cart) return;                  // failed read is a no-op, never a signal
      changeListeners.forEach(function (listener) {
        try { listener(cart); } catch (e) { log('cart listener threw', e); }
      });
    });
  }

  function onCartChange(listener) {
    if (typeof listener === 'function') changeListeners.push(listener);
  }

  function requestUrl(input) {
    try {
      if (typeof input === 'string') return input;
      if (input && typeof input.url === 'string') return input.url;
    } catch (e) {}
    return '';
  }

  function observeRequests() {
    try {
      var nativeFetch = window.fetch;
      if (typeof nativeFetch === 'function') {
        window.fetch = function (input) {
          var isCart = CART_PATH_RE.test(requestUrl(input));
          var result = nativeFetch.apply(this, arguments);
          if (isCart && result && typeof result.then === 'function') {
            result.then(scheduleChange, scheduleChange);
          }
          return result;
        };
      }

      var nativeOpen = XMLHttpRequest.prototype.open;
      XMLHttpRequest.prototype.open = function (method, url) {
        try {
          // Assign unconditionally, never "if (match) = true". An XHR object can
          // be reopened against a different URL; a sticky true would make a
          // later non-cart request on the same instance trigger a cart re-read.
          this.__pacCartRequest = CART_PATH_RE.test(String(url));
        } catch (e) {}
        return nativeOpen.apply(this, arguments);
      };

      var nativeSend = XMLHttpRequest.prototype.send;
      XMLHttpRequest.prototype.send = function () {
        try {
          // Re-adding the same function reference is deduped by the DOM, so a
          // reused XHR cannot accumulate listeners or fire scheduleChange twice.
          if (this.__pacCartRequest) this.addEventListener('loadend', scheduleChange);
        } catch (e) {}
        return nativeSend.apply(this, arguments);
      };
    } catch (e) {
      log('request observation unavailable; falling back to theme events', e);
    }
  }

  function observeEvents() {
    ['shapes:cart:update', 'shapes:cart:afteradditem', 'theme:update:cart']
      .forEach(function (name) { document.addEventListener(name, scheduleChange); });

    window.addEventListener('pageshow', scheduleChange);
    document.addEventListener('visibilitychange', function () {
      if (!document.hidden) scheduleChange();
    });
  }

  // ---- GiftGuard ----

  var MAX_INJECT_ATTEMPTS = 5;

  function injectPending(cart) {
    var record = State.get();
    var pending = record.giftPending;
    if (!pending || !pending.length) return;

    var toAdd = pendingAfterDedupe(cart, pending).filter(notSuperseded(cart));

    // Same reconciliation as applyRewards: pending ids already in the cart are
    // fulfilled and must be cleared even when others still need adding. Only
    // clearing in the all-present branch below would leave a landed gift
    // reported as still owed until some later cycle produced an empty toAdd.
    var alreadyPresent = idsNotIn(pending, toAdd);
    if (alreadyPresent.length) clearReserved(alreadyPresent);

    if (!toAdd.length) {
      State.patch({ giftPending: null, injectAttempts: null });
      return;
    }

    // A permanently dead variant would otherwise cost one POST on every cart
    // change, forever. Give up after a handful and drop the reservation.
    var attempts = (record.injectAttempts || 0) + 1;
    if (attempts > MAX_INJECT_ATTEMPTS) {
      log('giving up on unaddable gifts: ' + toAdd.join(', '));
      State.patch({ giftPending: null, injectAttempts: null });
      return;
    }
    State.patch({ injectAttempts: attempts });

    acquireLock();
    addGiftsBatch(toAdd).then(function (result) {
      clearReserved(result.added);
      if (!result.failed.length) State.patch({ injectAttempts: null });
      announceCartUpdate();
    });
  }

  /**
   * Remove gifts that the cart no longer justifies.
   *
   * Two tiers of removal, because two different rules are in play:
   *
   *   no paid item at all  -> remove EVERY gift, whatever granted it. This is
   *                           the checkout-for-shipping hole and it is absolute.
   *   below the popup's
   *   minimum subtotal     -> remove only Pick A Card gifts. That threshold is
   *                           the popup's own rule and has no authority over
   *                           the ladder, which qualifies by selling plan.
   */
  function guardGifts(cart) {
    var unpaidCart = !hasPaidItem(cart);
    var ourGifts = cart.items.filter(function (item) {
      if (!isOurGift(item)) return false;
      return unpaidCart || isGiftFrom(item, GIFT_VALUE);
    });
    if (!ourGifts.length) return;

    acquireLock();
    removeGiftLines(ourGifts).then(function (result) {
      if (!result) {
        // Removal failed: leave the gifts and retry next signal. Leaving a
        // gift too long is recoverable; a wrong removal is not.
        //
        // Deliberately uncapped, unlike injectPending's MAX_INJECT_ATTEMPTS.
        // The asymmetry is the point: giving up on an add costs the visitor a
        // gift they were promised, which is bounded and visible. Giving up on
        // a removal leaves $0.00 lines in a cart with nothing paid beside
        // them — the checkout-for-shipping hole this whole guard exists to
        // close. postJSON collapses every failure to null, so we cannot tell
        // a permanent error from a blip; retrying forever is the safe read of
        // that ambiguity, and the cost is one POST per disqualifying signal.
        releaseLock();
        return;
      }
      // Reserve only POPUP gifts. giftPending is the popup's owed-gifts list,
      // and injectPending re-adds from it as a Pick A Card gift — so putting a
      // ladder gift in there would quietly relabel it and hand it back on a
      // cart the ladder no longer entitles. The ladder needs no reservation:
      // it recomputes from the selling plan on every cart change.
      reserve(ourGifts
        .filter(function (item) { return isGiftFrom(item, GIFT_VALUE); })
        .map(function (item) { return item.variant_id; }));
      announceCartUpdate();
    });
  }

  // ---- supersession ----

  /**
   * Rules of the form "do not grant gift A while variant B is in the cart".
   *
   * Exists because another app can put the real product in the cart on its own
   * terms — Rebuy adds the Variety Pack at a subtotal threshold — and our
   * $0.00 gift copy of the same thing would then be a duplicate.
   *
   * Checked continuously rather than once at grant time, because the order is
   * not fixed: Rebuy's threshold can be crossed long after we granted the
   * gift. Evaluating on every cart change makes it self-correcting in both
   * directions — their line appears and ours goes, their line goes and ours
   * comes back.
   */
  var supersessions = [];

  function registerSupersessions(rules) {
    (rules || []).forEach(function (rule) {
      if (!rule || !rule.gift || !rule.supersededBy) return;
      supersessions.push({
        gift: String(rule.gift),
        supersededBy: String(rule.supersededBy)
      });
    });
  }

  function isSuperseded(cart, variantId) {
    if (!supersessions.length || !cart || !Array.isArray(cart.items)) return false;
    var id = String(variantId);
    return supersessions.some(function (rule) {
      if (rule.gift !== id) return false;
      return cart.items.some(function (item) {
        // Only a line we did NOT add counts as superseding. Our own gift copy
        // must never be read as the thing that replaces it.
        return String(item.variant_id) === rule.supersededBy && !isOurGift(item);
      });
    });
  }

  function notSuperseded(cart) {
    return function (variantId) { return !isSuperseded(cart, variantId); };
  }

  /**
   * Drop our gift lines that the cart has since superseded.
   *
   * Deliberately does NOT reserve what it removes. giftPending is the popup's
   * owed-gifts list and injectPending re-adds from it — reserving here would
   * add the gift straight back on the next signal, then remove it again, for
   * as long as the superseding line stayed in the cart.
   */
  function removeSuperseded(cart) {
    if (!supersessions.length) return;
    var doomed = cart.items.filter(function (item) {
      return isOurGift(item) && isSuperseded(cart, item.variant_id);
    });
    if (!doomed.length) return;

    acquireLock();
    removeGiftLines(doomed).then(function (result) {
      if (!result) { releaseLock(); return; }
      announceCartUpdate();
    });
  }

  /**
   * Foreign lines to withdraw so our own free copy can take their place.
   *
   * The inverse of supersession, and the only place this file removes a line
   * it did not add. Everywhere else isOurGift is a deliberately closed list,
   * precisely so another app's cart line can never be deleted by accident —
   * this is the exception, and it is only ever reached from an explicit
   * setting naming one product.
   *
   * The case it exists for: Rebuy's tiered progress bar adds the real Variety
   * Pack at a subtotal threshold. Withholding ours left the shopper with
   * Rebuy's copy, which is outside the ladder — it appears and disappears on
   * Rebuy's own threshold rather than on the plan the shopper bought.
   * Removing it hands the tier back to the ladder, which grants ours.
   *
   * Matched on Rebuy's own line properties, never on the variant alone. The
   * Variety Pack is also a product people buy outright, and the Good Mood PDP
   * adds it as its own 8-Pack gift — matching the variant deleted both of
   * those within a second of landing. If Rebuy ever renames its properties
   * this stops matching, and the shopper keeps Rebuy's copy: the safe way to
   * fail, where the loose match failed by deleting a paid line.
   *
   * And only while the cart earns our copy. Each rule carries a `when(cart)`
   * from its grantor; the ladder's says "this cart is on a plan that gets the
   * Variety Pack". Without it, Rebuy's tier gift was withdrawn from every
   * cart — a one-time Daily Dose order crossing Rebuy's threshold lost the
   * Variety Pack a second after Rebuy added it, with nothing given back. A
   * rule registered without `when` keeps the old unconditional behaviour.
   */
  var replacements = [];

  /** A line added by Rebuy's tiered progress bar, as observed on the live cart. */
  function isRebuyTierGift(item) {
    var props = item && item.properties;
    return !!(props && props._source === 'Rebuy' &&
      props._attribution === 'Rebuy Tiered Progress Bar');
  }

  // Rebuy does not re-add after a removal — measured on the live cart, no
  // re-add in ten seconds. The cap is for the app that one day does: two
  // systems each re-adding on the other's signal would be an endless loop of
  // cart writes, and a gift quietly not appearing beats that.
  var REPLACEMENT_LIMIT = 3;
  var replacementCount = 0;

  function registerReplacements(rules) {
    (rules || []).forEach(function (rule) {
      if (!rule || !rule.replace) return;
      replacements.push({
        id: String(rule.replace),
        when: typeof rule.when === 'function' ? rule.when : null
      });
    });
  }

  /** Is this line one the cart currently wants withdrawn for our copy? */
  function isReplaceable(cart, item) {
    if (!isRebuyTierGift(item)) return false;
    var id = String(item.variant_id);
    return replacements.some(function (rule) {
      if (rule.id !== id) return false;
      if (!rule.when) return true;
      try { return !!rule.when(cart); } catch (e) {
        // A grantor that cannot answer leaves the other app's line alone.
        log('replacement condition threw', e);
        return false;
      }
    });
  }

  function removeReplaced(cart) {
    if (!replacements.length || !cart || !Array.isArray(cart.items)) return;
    if (replacementCount >= REPLACEMENT_LIMIT) return;

    var doomed = cart.items.filter(function (item) {
      // Only Rebuy's copy, which also rules out our own — the line this
      // exists to make room for — and only when the cart earns ours.
      return isReplaceable(cart, item);
    });
    if (!doomed.length) return;

    replacementCount += 1;
    if (replacementCount === REPLACEMENT_LIMIT) {
      log('replacement limit reached; leaving the other app\'s line alone from here');
    }

    acquireLock();
    removeGiftLines(doomed).then(function (result) {
      if (!result) { releaseLock(); return; }
      announceCartUpdate();
    });
  }

  // ---- gift reconciliation for other sources ----

  /**
   * Bring one source's gift lines in line with what it wants.
   *
   * Adds anything wanted that is not already in the cart, removes anything of
   * this source's that is no longer wanted, and leaves every other source
   * alone. This is the only way a second feature should touch gift lines —
   * the cart layer stays the single owner of cart mutations, so there is one
   * lock and one dedupe rather than two systems racing.
   *
   * Dedupe is by variant ACROSS sources: if the popup already granted the
   * Sleep Guide, the ladder will not add a second one. That is why the ladder
   * gifts being "similar to the popup ones" is safe.
   *
   * @param {string} source - property value identifying the grantor
   * @param {Array} wantedVariantIds
   * @returns {Promise<{added: Array, removed: Array, skipped: Array}>}
   */
  function reconcileGifts(source, wantedVariantIds) {
    var outcome = { added: [], removed: [], skipped: [] };
    if (!source || source === GIFT_VALUE) {
      log('reconcileGifts needs its own source name, not the popup\'s');
      return Promise.resolve(outcome);
    }

    if (GIFT_SOURCES.indexOf(source) === -1) {
      // An unrecognised source would be added but never matched by isOurGift,
      // so it could never be removed or guarded — an orphan $0.00 line.
      log('unknown gift source "' + source + '"; add it to GIFT_SOURCES first');
      return Promise.resolve(outcome);
    }

    var wanted = (wantedVariantIds || []).map(String);

    return readCart().then(function (cart) {
      // Failed read: do nothing. Never guess at gift state.
      if (!cart) return outcome;

      // hasPaidItem, NOT qualifies. The popup's minimum-subtotal threshold is
      // the popup's rule; gating other sources on it meant a $99 subscription
      // was refused its gifts by a $130 setting that had nothing to do with
      // the ladder. A cart with no paid line still gets nothing — GiftGuard
      // clears it on this same signal, so adding here would fight it.
      if (!hasPaidItem(cart)) return outcome;

      var presentAnySource = {};
      var mineByVariant = {};
      cart.items.forEach(function (item) {
        if (!isOurGift(item)) return;
        presentAnySource[String(item.variant_id)] = true;
        if (isGiftFrom(item, source)) mineByVariant[String(item.variant_id)] = item;
      });

      var toAdd = wanted.filter(function (id) {
        if (presentAnySource[id]) { outcome.skipped.push(id); return false; }
        if (isSuperseded(cart, id)) { outcome.skipped.push(id); return false; }
        return true;
      });

      var toRemove = Object.keys(mineByVariant)
        .filter(function (id) { return wanted.indexOf(id) === -1; })
        .map(function (id) { return mineByVariant[id]; });

      if (!toAdd.length && !toRemove.length) return outcome;

      acquireLock();
      var work = toRemove.length ? removeGiftLines(toRemove) : Promise.resolve({});
      return work.then(function (removeResult) {
        if (removeResult) {
          outcome.removed = toRemove.map(function (i) { return i.variant_id; });
        }
        if (!toAdd.length) return outcome;
        return addGiftsBatch(toAdd, source).then(function (addResult) {
          outcome.added = addResult.added;
          return outcome;
        });
      }).then(function () {
        announceCartUpdate();
        return outcome;
      });
    });
  }

  function handleCartChange(cart) {
    if (qualifies(cart)) injectPending(cart);
    else guardGifts(cart);
    // After the guard, so a cart losing its last paid line drops every gift
    // outright rather than removing one here and the rest a signal later.
    removeSuperseded(cart);
    // Last: withdrawing the other app's copy is what lets the ladder grant
    // ours, and it should only happen once the cart is otherwise settled.
    removeReplaced(cart);
  }

  // ---- public surface: the one and only assignment site ----

  window.THHPickACard = {
    version: 3,
    GIFT_PROPERTY: GIFT_PROPERTY,
    GIFT_VALUE: GIFT_VALUE,

    /** Called once by sections/pick-a-card.liquid with parsed section settings. */
    init: function (options) {
      if (options && typeof options.minimumSubtotalCents === 'number') {
        config.minimumSubtotalCents = options.minimumSubtotalCents;
      }
      start();
    },

    state: State,
    readCart: readCart,
    qualifies: qualifies,
    onCartChange: onCartChange,
    applyRewards: applyRewards,

    /** Bring one source's gift lines in line with what it wants. See above. */
    reconcileGifts: reconcileGifts,

    /** Rules: [{ gift: variantId, supersededBy: variantId }]. See above. */
    registerSupersessions: registerSupersessions,
    registerReplacements: registerReplacements,

    /** Debug readers for the verification snippets. Safe to ship. */
    __debug: {
      lockHeld: function () { return lockHeld; },
      cycleCount: function () { return cycleCount; },
      acquire: function () { acquireLock(); },
      announce: function () { announceCartUpdate(); }
    }
  };

  function start() {
    if (started) return;
    started = true;
    observeRequests();
    observeEvents();
    onCartChange(handleCartChange);
    // Evaluate once on load: gifts can be orphaned by a change made in
    // another tab, or on the cart page before any signal fires. Route the
    // initial read through the same lock guard the signal path uses.
    scheduleChange();
  }

  // Always DOMContentLoaded, never an immediate call. A defer script executes
  // at readyState "interactive", so an immediate branch would always win the
  // race and start() would run before the section could call init() with the
  // real minimumSubtotalCents — leaving the threshold at 0 forever.
  document.addEventListener('DOMContentLoaded', function () {
    if (!started) start();
  });
})();
